import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, spacing, breakpoints } from '../../../constants/theme';
import { parseFlashCheckFileName } from '../../../utils/fileUtils';

const ViewerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.background.primary};
  z-index: 2000;
  display: flex;
  flex-direction: column;
`;

const ViewerHeader = styled.div`
  background: ${colors.background.secondary};
  border-bottom: 1px solid ${colors.background.tertiary};
  padding: ${spacing.md} ${spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 2001;

  @media (max-width: ${breakpoints.tablet}) {
    padding: ${spacing.sm} ${spacing.md};
    flex-wrap: wrap;
    gap: ${spacing.sm};
  }
`;

const ViewerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  flex: 1;
  min-width: 0;

  @media (max-width: ${breakpoints.tablet}) {
    order: 2;
    width: 100%;
    margin-top: ${spacing.sm};
  }
`;

const ViewerFileName = styled.h2`
  color: ${colors.text.primary};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 16px;
  }
`;

const ViewerActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};

  @media (max-width: ${breakpoints.tablet}) {
    order: 1;
    gap: ${spacing.sm};
  }
`;

const ActionButton = styled.button`
  background: ${colors.background.tertiary};
  border: 1px solid ${colors.background.tertiary};
  color: ${colors.text.primary};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.main}20;
    border-color: ${colors.primary.main};
    color: ${colors.primary.light};
  }

  &.primary {
    background: ${colors.primary.main};
    border-color: ${colors.primary.main};
    color: ${colors.text.primary};

    &:hover {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: ${spacing.xs} ${spacing.sm};
    font-size: 12px;
  }
`;

const ViewerContent = styled.div`
  flex: 1;
  background: ${colors.background.primary};
  overflow: hidden;
  position: relative;
`;

const HTMLFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.background.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.background.tertiary};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${colors.text.secondary};
  font-size: 16px;
  margin: 0;
`;

const ErrorContainer = styled.div`
  padding: ${spacing.xl};
  text-align: center;
  color: ${colors.error};
`;

interface HTMLViewerProps {
  fileUrl: string | null;
  fileName: string;
  onClose: () => void;
}

export const HTMLViewer: React.FC<HTMLViewerProps> = ({
  fileUrl,
  fileName,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Format the filename for display using the parsing utility
  const getDisplayName = () => {
    const parsed = parseFlashCheckFileName(fileName);
    return parsed ? parsed.displayName : fileName;
  };

  useEffect(() => {
    if (fileUrl) {
      setLoading(true);
      setError(null);
    }
  }, [fileUrl]);

  // Handle Escape key to close viewer
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleFrameLoad = () => {
    setLoading(false);
  };

  const handleFrameError = () => {
    setLoading(false);
    setError('Failed to load the HTML document. The file may be corrupted or inaccessible.');
  };

  const handleOpenInNewTab = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  if (!fileUrl) return null;

  return (
    <ViewerContainer>
      <ViewerHeader>
        <ViewerTitle>
          <span style={{ fontSize: '18px' }}>📄</span>
          <ViewerFileName>{getDisplayName()}</ViewerFileName>
        </ViewerTitle>
        
        <ViewerActions>
          <ActionButton onClick={handleOpenInNewTab} title="Open in new tab">
            🌐 New Tab
          </ActionButton>
          <ActionButton className="primary" onClick={onClose} title="Close viewer">
            ✕ Close
          </ActionButton>
        </ViewerActions>
      </ViewerHeader>

      <ViewerContent>
        {loading && (
          <LoadingOverlay>
            <LoadingSpinner />
            <LoadingText>Loading {getDisplayName()}...</LoadingText>
          </LoadingOverlay>
        )}

        {error ? (
          <ErrorContainer>
            <h3>⚠️ Failed to Load Document</h3>
            <p>{error}</p>
            <ActionButton onClick={handleOpenInNewTab} style={{ marginTop: spacing.lg }}>
              🌐 Try Opening in New Tab
            </ActionButton>
          </ErrorContainer>
        ) : (
          <HTMLFrame
            src={fileUrl}
            onLoad={handleFrameLoad}
            onError={handleFrameError}
            title={getDisplayName()}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        )}
      </ViewerContent>
    </ViewerContainer>
  );
};

export default HTMLViewer;
